import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { postsAPI } from '../api';
import { Calendar, ArrowRight, UtensilsCrossed } from 'lucide-react';

const POSTS_PER_PAGE = 24;
const CATEGORY = 'food-drink';

const FoodDrinkPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * POSTS_PER_PAGE;
      const response = await postsAPI.getAll(CATEGORY, POSTS_PER_PAGE, false, skip);
      setPosts((response.data || []).filter((p) => p.published));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  useEffect(() => {
    postsAPI.getCount(CATEGORY)
      .then((r) => setTotalPosts(r.data.total || 0))
      .catch(() => {});
  }, []);

  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);

  return (
    <div className="min-h-screen bg-white" data-testid="food-drink-page">
      <SEO
        title="Paisley Food & Drink Reviews | Paisley.org.uk"
        description="Honest, first-hand reviews of Paisley's cafes, restaurants, pubs and takeaways — from the paisley.org.uk team."
        keywords="Paisley food, Paisley restaurants, Paisley cafes, Paisley pubs, Renfrewshire food reviews, where to eat in Paisley"
        url="/food-drink"
      />
      <Header />

      <section className="bg-gradient-to-br from-amber-900 via-orange-800 to-rose-900 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4">
            <UtensilsCrossed className="text-amber-200" size={16} />
            <span className="text-amber-100 text-sm font-semibold uppercase tracking-wider">Local reviews</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="food-drink-page-title">
            Paisley Food &amp; <span className="text-amber-300">Drink</span>
          </h1>
          <p className="text-base lg:text-lg text-orange-100 max-w-2xl mx-auto">
            Honest reviews of every cafe, restaurant, pub and takeaway worth writing about in Paisley.
          </p>
          {totalPosts > 0 && (
            <p className="text-sm text-orange-200/80 mt-3">{totalPosts.toLocaleString()} review{totalPosts === 1 ? '' : 's'}</p>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Loading reviews…</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <UtensilsCrossed size={48} className="text-amber-200 mx-auto mb-3" />
              <p className="text-slate-600 text-lg">No reviews published yet.</p>
              <p className="text-slate-500 text-sm mt-1">Check back soon — the paisley.org.uk team is out and about tasting the town.</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/news/${post.slug}`}
                    className="group"
                    data-testid={`food-drink-card-${post.slug}`}
                  >
                    <div className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-amber-400 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="relative overflow-hidden h-40 bg-gradient-to-br from-amber-100 to-rose-100">
                        {post.featured_image && (
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                          />
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                          <Calendar size={12} />
                          <span>{new Date(post.created_at).toLocaleDateString('en-GB')}</span>
                        </div>
                        <h3 className="text-sm font-bold text-slate-900 mb-2 group-hover:text-amber-700 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-slate-600 text-xs mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center text-amber-700 text-xs font-medium group-hover:text-amber-800">
                          Read review
                          <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" size={12} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="food-drink-pagination">
                  <button
                    onClick={() => { setPage((p) => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-40 transition-colors text-sm"
                  >Previous</button>
                  <span className="px-4 py-2 text-sm text-slate-600">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-40 transition-colors text-sm"
                  >Next</button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FoodDrinkPage;
